import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { StopwatchIcon } from '@sorare/core/src/atoms/icons/StopwatchIcon/index.tsx';
import {
  timeKeyFromProgress,
  type TimeKey,
} from '@sorare/core/src/atoms/icons/colorsByTime.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import {
  BodyL,
  BodyS,
  HeadlineM,
} from '@sorare/core/src/atoms/typography/index.tsx';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import { glossary } from '@sorare/core/src/lib/glossary.ts';

import type { ClockDialogContent_thresholdPickerTask } from './__generated__/index.graphql.ts';

type Props = {
  task: ClockDialogContent_thresholdPickerTask;
  onClose: () => void;
};

export const ClockDialogContent = ({ task, onClose }: Props) => {
  const { expirationProgressBasisPoints: timeProgress } = task;
  const timeKey: TimeKey =
    timeProgress === null ? 'locked' : timeKeyFromProgress(timeProgress);

  if (timeKey === '100') {
    return (
      <Vertical center style={{ padding: 'var(--double-unit)' }} gap={2}>
        <StopwatchIcon
          style={{ zIndex: 1 }}
          timeProgress={timeProgress}
          size={7}
        />
        <Vertical center>
          <HeadlineM>
            <FormattedMessage defaultMessage="Your streak ended" />
          </HeadlineM>
          <BodyL className="text-center">
            <FormattedMessage defaultMessage="Your timer ran out before you cleared the next level." />
          </BodyL>
        </Vertical>
        <Button size="medium" color="quaternary" onClick={onClose} fullWidth>
          <FormattedMessage {...glossary.gotIt} />
        </Button>
      </Vertical>
    );
  }

  if (timeKey === 'locked') {
    return (
      <Vertical center style={{ padding: 'var(--double-unit)' }} gap={2}>
        <StopwatchIcon
          style={{ zIndex: 1 }}
          timeProgress={timeProgress}
          size={7}
        />
        <Vertical center>
          <HeadlineM>
            <TimeLeft time={task.wouldExpireAt ?? new Date()} />
          </HeadlineM>
          <BodyL className="text-center">
            <FormattedMessage defaultMessage="Your level is unlocked. Set a lineup to lock it in and start your clock. Every win pushes your deadline to the end of the next match day." />
          </BodyL>
        </Vertical>
        <Button size="medium" color="quaternary" onClick={onClose} fullWidth>
          <FormattedMessage {...glossary.gotIt} />
        </Button>
        <BodyS>
          <FormattedMessage defaultMessage="Skip a game week, no penalty." />
        </BodyS>
      </Vertical>
    );
  }

  return (
    <Vertical center style={{ padding: 'var(--double-unit)' }} gap={2}>
      <StopwatchIcon
        style={{ zIndex: 1 }}
        timeProgress={timeProgress}
        size={7}
      />
      <Vertical center>
        <HeadlineM>
          <TimeLeft time={task.expiresAt ?? new Date()} />
        </HeadlineM>
        <BodyL className="text-center">
          <FormattedMessage defaultMessage="Your level is locked in. Every win pushes your deadline to the end of the next match day." />
        </BodyL>
      </Vertical>
      <Button size="medium" color="tertiary" onClick={onClose} fullWidth>
        <FormattedMessage {...glossary.gotIt} />
      </Button>
      <BodyS>
        <FormattedMessage defaultMessage="Run out of time, your streak resets to level 1." />
      </BodyS>
    </Vertical>
  );
};

ClockDialogContent.fragments = {
  thresholdPickerTask: gql`
    fragment ClockDialogContent_thresholdPickerTask on ThresholdPickerTask {
      id
      expiresAt
      wouldExpireAt
      expirationProgressBasisPoints
    }
  ` as TypedDocumentNode<ClockDialogContent_thresholdPickerTask>,
};
